import { BackButton } from '@/components/BackButton';
import IdWithCopy from '@/components/IdWithCopy';
import type { FC, PropsWithChildren, ReactNode } from 'react';

/**
 * The frame every admin page sits in: a slate ground with one gap-6 column.
 *
 * The width is the only knob, and it defaults to the whole window — this is an admin tool full of
 * wide tables, and a column that stops at 72rem sends them into a horizontal scroll while the screen
 * sits half empty. The narrower settings are for pages that are one column of prose or form fields,
 * where a 2000px line is worse than a short one. Everything else is fixed on purpose, so pages don't
 * drift apart one padding value at a time.
 */
export const PageShell: FC<PropsWithChildren<{ width?: 'full' | 'wide' | 'medium' | 'narrow' }>> = ({
    width = 'full',
    children,
}) => (
    <div className="min-h-screen bg-slate-50">
        <div className={`mx-auto flex flex-col gap-6 p-6 ${WIDTHS[width]}`}>{children}</div>
    </div>
);

/**
 * The top of a page: what you're looking at, and how to get back out of it. `children` become the
 * actions on the right (search boxes, filters, an Add button).
 */
export const PageHeader: FC<
    PropsWithChildren<{
        title: ReactNode;
        description?: ReactNode;
        /** Accessible label for the back arrow shown left of the title; omit for no arrow. */
        backLabel?: string;
        /** The record's id, shown under the title with a copy button. */
        id?: string;
    }>
> = ({ title, description, backLabel, id, children }) => (
    <div className="flex flex-wrap items-start gap-3">
        <div className="flex min-w-0 flex-1 basis-[28rem] items-start gap-3">
            {backLabel && <BackButton label={backLabel} className="mt-1" />}
            <div className="min-w-0">
                <h1 className="text-3xl font-semibold">{title}</h1>
                {id && <IdWithCopy id={id} variant="muted" className="text-sm" showLabel />}
                {description && <div className="page-description">{description}</div>}
            </div>
        </div>
        {children && <div className="ml-auto flex flex-wrap items-center gap-2">{children}</div>}
    </div>
);

/** Spelled out rather than composed, so Tailwind can still see the class names. */
const WIDTHS = {
    full: 'max-w-none',
    wide: 'max-w-6xl',
    medium: 'max-w-5xl',
    narrow: 'max-w-4xl',
} as const;
